import { useCallback, useMemo, useState } from 'react'
import { PresentationLayout, PresentationSlideState } from '@/api/presentations/presentations'
import type {
  PresentationFitPolicy,
  PresentationSlide,
  UpdatePresentationSlideRequest
} from '@/api/presentations/presentations'
import { evaluatePresentationFit } from '@/components/presentation/presentationFit'

export type PresentationLayoutValue = PresentationSlide['layout']

export type PresentationSlideDraft = Pick<
  PresentationSlide,
  'title' | 'keyMessage' | 'contentPoints' | 'layout' | 'visualDirection' | 'speakerNotes'
>

function seedDraft(slide: PresentationSlide, isCover: boolean): PresentationSlideDraft {
  return {
    title: slide.title,
    keyMessage: slide.keyMessage,
    contentPoints: isCover ? [] : [...slide.contentPoints],
    layout: isCover ? PresentationLayout.SectionHeader : slide.layout,
    visualDirection: slide.visualDirection,
    speakerNotes: slide.speakerNotes
  }
}

type PresentationSlideDraftState = {
  slideId: number
  active: boolean
  draft: PresentationSlideDraft
}

export function usePresentationSlideDraft({
  slide,
  fitPolicy,
  isCover,
  active
}: {
  slide: PresentationSlide
  fitPolicy: PresentationFitPolicy
  isCover: boolean
  active: boolean
}) {
  const [state, setState] = useState<PresentationSlideDraftState>(() => ({
    slideId: slide.slideId,
    active,
    draft: seedDraft(slide, isCover)
  }))
  const slideChanged = state.slideId !== slide.slideId
  const enteringEdit = active && !state.active

  if (slideChanged || state.active !== active) {
    setState({
      slideId: slide.slideId,
      active,
      draft: slideChanged || enteringEdit ? seedDraft(slide, isCover) : state.draft
    })
  }

  const draft = isCover ? { ...state.draft, contentPoints: [], layout: PresentationLayout.SectionHeader } : state.draft
  const fit = useMemo(
    () =>
      evaluatePresentationFit(
        {
          title: draft.title,
          keyMessage: draft.keyMessage,
          contentPoints: draft.contentPoints,
          layout: draft.layout
        },
        fitPolicy
      ),
    [draft.contentPoints, draft.keyMessage, draft.layout, draft.title, fitPolicy]
  )

  const updateDraft = useCallback((update: (draft: PresentationSlideDraft) => PresentationSlideDraft) => {
    setState((current) => ({ ...current, draft: update(current.draft) }))
  }, [])
  const setTitle = useCallback((title: string) => updateDraft((current) => ({ ...current, title })), [updateDraft])
  const setKeyMessage = useCallback(
    (keyMessage: string) => updateDraft((current) => ({ ...current, keyMessage })),
    [updateDraft]
  )
  const setPoint = useCallback(
    (index: number, value: string) =>
      updateDraft((current) => ({
        ...current,
        contentPoints: current.contentPoints.map((point, at) => (at === index ? value : point))
      })),
    [updateDraft]
  )
  const addPoint = useCallback(
    () => updateDraft((current) => ({ ...current, contentPoints: [...current.contentPoints, ''] })),
    [updateDraft]
  )
  const removePoint = useCallback(
    (index: number) =>
      updateDraft((current) => ({
        ...current,
        contentPoints: current.contentPoints.filter((_, at) => at !== index)
      })),
    [updateDraft]
  )
  const setLayout = useCallback(
    (layout: PresentationLayoutValue) => updateDraft((current) => ({ ...current, layout })),
    [updateDraft]
  )
  const setVisualDirection = useCallback(
    (visualDirection: string) => updateDraft((current) => ({ ...current, visualDirection })),
    [updateDraft]
  )
  const setSpeakerNotes = useCallback(
    (speakerNotes: string) => updateDraft((current) => ({ ...current, speakerNotes })),
    [updateDraft]
  )
  const reset = useCallback(
    () => setState((current) => ({ ...current, draft: seedDraft(slide, isCover) })),
    [isCover, slide]
  )
  const request = useCallback(
    (): UpdatePresentationSlideRequest => ({
      title: draft.title.trim(),
      keyMessage: draft.keyMessage.trim(),
      contentPoints: draft.contentPoints.map((point) => point.trim()).filter((point) => point.length > 0),
      layout: draft.layout,
      visualDirection: draft.visualDirection.trim(),
      speakerNotes: draft.speakerNotes.trim(),
      sourceDocIds: slide.sourceDocIds,
      sourceArtifactIds: slide.sourceArtifactIds,
      state: PresentationSlideState.Ready
    }),
    [draft, slide.sourceArtifactIds, slide.sourceDocIds]
  )

  return {
    draft,
    fit,
    setTitle,
    setKeyMessage,
    setPoint,
    addPoint,
    removePoint,
    setLayout,
    setVisualDirection,
    setSpeakerNotes,
    reset,
    request
  }
}
